/**
 * ImageGeneratorNode (Canvas V2 — Sprint 9i migration)
 *
 * Migrated to UnifiedBottomPanel with 3 tabs: Config, Refine, Variants.
 *
 * Changes from pre-migration:
 * - Removed inline config section (renderConfig) — all config lives in Config tab
 * - Removed ImageHistoryPopover — Variants tab replaces it
 * - Removed HistoryBadgeButton — no longer needed
 * - Both standalone and group mode use UnifiedBottomPanel
 * - Card shows only the image display area + toolbar
 * - Loaded asset dimensions drive card width/aspect; config drives placeholders
 */

import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { NODE_CARD_RADIUS_CLASS } from '../shared/constants'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import * as Y from 'yjs'
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow'
import { AlertTriangle, BookmarkPlus, Check, Loader2 } from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from '../shared/MetaLoadingSurface'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import { executeNode, InsufficientSparksError } from '../../../../api/client'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useCanvasOptional } from '../../CanvasContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import type { NodeDataRecord } from '../../nodeRegistry'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { createNadDebug } from '../../../../utils/debugLog'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { getImageModelAlias } from '../../../../constants/modelAliases'
import { dataStatus,dataStrOpt, type ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  IMAGE_GEN_TABS,
  IMAGE_MODEL_OPTIONS,
  DEFAULT_TAB,
  FlatIdleNodeCard,
  ConfigTabContent,
  RefineTabContent,
  VariantsTabContent,
  computeVariantPanelLayout,
  DESIGNER_PANEL_MIN_WIDTH,
  QuickInfoBar,
  useNodeActions,
  useVariantImageDownload,
  useSaveToLibrary,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  ASPECT_RATIO_MAP,
  ASPECT_WIDTH_MAP,
  CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT,
  getCanvasNodeCardIntrinsicStyle,
  getPreviewIntrinsicHeight,
  snapNaturalDimensionsToDisplayDimensions,
  useCanvasNodeImage,
  generatedImageHistoryToJson,
  generatedImagePath,
  getPickedIndex,
  type SnappedDisplayDimensions,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  createHandlesFromSchema,
  encodeNodeError,
  resolveNodeErrorMessage,
} from '../shared'
import type { HistoryEntry } from '../shared/types'
import { localizeDefaultNodeName } from '../shared/defaultNodeNames'
import { AnnotationEditorOverlay, useImageAnnotation } from '../shared/annotate'
import {
  ASPECT_RATIO_VALUES,
  FALLBACK_ASPECT_RATIOS,
  getImageConstraints,
  snapOnModelChange,
  snapToModelConstraints,
} from '../shared/imageConstraints'
import type { ImageConstraintNotice } from '../shared/ConfigTabContent'
import {
  NODE_SCHEMAS_BY_TYPE,
  type AspectRatio,
  type ReactFlowCanvasNodeDataByType,
} from '../../../../generated/nodeDataTypes'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

// ─── Constants ────────────────────────────────────────────────────────────────

// Derived, not declared: the per-model offer lives on the capability record
// (RFC-1202 phase 4). This is the model-less grid used where no selection is
// in scope; per-model gating goes through getImageConstraints(model).
const ASPECT_RATIO_OPTIONS: readonly AspectRatio[] = FALLBACK_ASPECT_RATIOS
const imageGeneratorSchema = NODE_SCHEMAS_BY_TYPE.imageGenerator
type ImageGeneratorReactFlowNodeData = ReactFlowCanvasNodeDataByType['imageGenerator']

// Dev-gated (VITE_NAD_DEBUG) trace of per-model quality/aspect snapping so the
// demo timeline shows the selector self-correcting when the model changes and
// when a stale saved node is normalized on mount.
const imageSelectorGatingLog = createNadDebug('dw:image-selector-gating')

// Accept every schema AspectRatio; a hardcoded subset here read GPT Image's
// 2:1 / 21:9 / Auto as 16:9, so a model switch snapped from the wrong value.
function normalizeAspectRatio(value: string | undefined): AspectRatio {
  return ASPECT_RATIO_VALUES.find((ratio) => ratio === value) ?? '16:9'
}

const HANDLES = createHandlesFromSchema(imageGeneratorSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_upstream: { position: Position.Top },
    in_prompt: { style: { top: '62%' } },
    in_image: { style: { top: '74%' } },
  },
})

// Storyboard panels render no card border of their own, so there is no own-node
// border for the variant picker to match. Inherit the parent Storyboard
// Designer's identity rather than leaving the picker on an orphan purple from
// the legacy ACCENT_COLORS palette (post 1579940090429955).
const agentColor = AGENT_COLORS.storyboard

const quickInfoLayoutLog = createNadDebug('dw:image-generator:quick-info-layout')
const variantsPanelLog = createNadDebug('dw:variants-panel')
const canvasNodeIntrinsicSizeLog = createNadDebug('dw:canvas-node-intrinsic-size')
const refineVariantCountLog = createNadDebug('dw:refine-variant-count')

// ─── Data shape ───────────────────────────────────────────────────────────────

import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { generateModeFor, modelSourceOf, trackVariantKeptChange, type ExecuteNodeAnalytics } from '../../../../analytics/nodeAnalytics'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { useGenerationGracePeriod } from '../../../../features/generationGracePeriod/useGenerationGracePeriod'

// ─── Component ────────────────────────────────────────────────────────────────

const ImageGeneratorNode = memo((props: NodeProps<ImageGeneratorReactFlowNodeData>) => {
  const nodePresentationWidth = useNodePresentationWidth()
  const { previewStyle, mediaFit } = useNodePresentationPreview()
  const { t } = useTranslation('canvas')
  const { id, type, data, selected, dragging } = props
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()

  const { setNodes, getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const canvas = useCanvasOptional()
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const node = useCanvasNode(id, 'imageGenerator', data)

  const out_image = node?.get('out_image')
  const in_name = node?.get('in_name')
  // "Panel 1" / "Grid 1" are Director-assigned defaults stored in English (the
  // backend re-parses them for ordinals and @-mention lookup), so they are
  // localized on the way out only. Returns undefined once the user renames.
  const displayName =
    localizeDefaultNodeName('imageGenerator', { in_name, name_autogen: node?.get('name_autogen') }, t) ??
    in_name
  const in_prompt = node?.get('in_prompt') ?? ''
  const in_aspect_ratio = normalizeAspectRatio(node?.get('in_aspect_ratio'))
  const in_style = node?.get('in_style')
  const in_quality = node?.get('in_quality') ?? '1K'
  const in_model = node?.get('in_model') ?? 'gemini-3.1-flash-image-preview'
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat } = useNodeActions({ nodeId: id })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, displayName || t('imageGeneratorNode.imageGenerator'))
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId: id, selected })
  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)

  const [assetDisplayDimensions, setAssetDisplayDimensions] = useState<SnappedDisplayDimensions | null>(null)
  const pickedIndex = getPickedIndex(out_image)

  // Build image URL from out_image history
  let imagePath: string | undefined
  const genImageHistory = out_image?.history ?? []
  if (genImageHistory.length) {
    const idx = getPickedIndex(out_image)
    imagePath = generatedImagePath(genImageHistory[idx])
  }
  useEffect(() => {
    setAssetDisplayDimensions(null)
  }, [imagePath, pickedIndex])

  const requestedAspectRatio = ASPECT_RATIO_MAP[in_aspect_ratio] ?? '16/9'
  const cssAspectRatio = assetDisplayDimensions?.aspectRatio ?? requestedAspectRatio
  const nodeWidth = assetDisplayDimensions?.width ?? ASPECT_WIDTH_MAP[in_aspect_ratio] ?? 315

  const cacheBust = cacheKey
  const imageUrl =
    imagePath && projectId
      ? getThumbnailUrl(projectId, imagePath, 0, cacheBust)
      : null

  const previewRef = useRef<HTMLDivElement | null>(null)
  const fullImageUrl =
    imagePath && projectId
      ? getAssetUrl(projectId, imagePath, 0, cacheBust)
      : null

  // RFC-1115: annotate the full-resolution asset (not the thumbnail — the bake
  // reads the original, and a thumbnail may not preserve the exact aspect).
  const annotate = useImageAnnotation({
    nodeId: id,
    feNodeType: 'imageGenerator',
    imagePath,
    imageUrl: fullImageUrl,
    projectId,
  })

  const isRunning = status === 'running'
  const generationGrace = useGenerationGracePeriod({
    projectId,
    canvasId,
    nodeId: id,
    nodeType: 'image_generator',
    isBackendRunning: isRunning,
  })
  const isGenerating = isRunning || generationGrace.isLocallyGenerating
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isGenerating || isPendingExecution || isPendingCost
  const shouldRenderNodeChrome =
    !lightweightNodeChrome || !!selected || isHoveringLightweightNodeChrome

  // Canvas cards fetch the full-resolution asset, and ReactFlow mounts every
  // in-viewport node at once, so a zoomed-out dense canvas would otherwise
  // fire hundreds of concurrent full-size requests and paint top-down. Wait
  // our turn in the shared nearest-first queue (post 1596425285448102).
  //
  // `enabled` mirrors the two branches below that render something OTHER than
  // the gated <img>: the spinner (generating / queued / pending-cost) and the
  // lightweight-chrome early return. Neither can fire onMediaSettled, so
  // reserving a slot for them would strand it — eight stranded slots exhaust
  // the pool and blank the whole canvas, which is worse than the original bug.
  const willRenderGatedPreview = !showSpinner && (shouldRenderNodeChrome || !!dragging)
  const { displayUrl: previewUrl, onMediaSettled } = useCanvasNodeImage(
    imageUrl,
    previewRef,
    { enabled: willRenderGatedPreview },
  )

  const loadingMessage = isGenerating
    ? (progressMessage || t('imageGeneratorNode.creatingImage'))
    : isPendingCost
      ? t('imageGeneratorNode.waitingForCostApproval')
      : t('imageGeneratorNode.queuedWaiting')
  const metaLoading = useMemo<MetaLoadingDescriptor | undefined>(() => {
    if (!showSpinner) return undefined
    return {
      message: loadingMessage,
      detail: isGenerating ? t('imageGeneratorNode.mayTakeSeconds') : undefined,
      nodeType: 'imageGenerator',
      loadingState: isPendingCost
        ? 'pending-cost'
        : isPendingExecution
          ? 'pending-execution'
          : generationGrace.isLocallyGenerating && !isRunning
            ? 'local-grace'
            : 'running',
    }
  }, [generationGrace.isLocallyGenerating, isGenerating, isPendingCost, isPendingExecution, isRunning, loadingMessage, showSpinner, t])
  const cancelDuringGraceButton = generationGrace.isWaiting && !isRunning ? (
    <button
      onClick={(event) => {
        event.stopPropagation()
        generationGrace.cancelPending()
      }}
      className="nodrag mt-3 flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      data-testid="cancel-node-button"
      title={t('imageGeneratorNode.stop')}
    >
      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="6" width="12" height="12" rx="1" />
      </svg>
      <span className="text-xxs font-medium">{t('imageGeneratorNode.stop')}</span>
    </button>
  ) : null
  const isError = status === 'error'
  const previewIntrinsicHeight = getPreviewIntrinsicHeight(nodeWidth, cssAspectRatio)
  // Always reserve the QuickInfoBar footer height, even while the spinner shows,
  // so a rendering card is the same height as a completed one (post 1539881217769176).
  // Still 0 when node chrome is not rendered (lightweight mode) — the bar is hidden then.
  const quickInfoIntrinsicHeight =
    !shouldRenderNodeChrome ? 0 : CANVAS_NODE_QUICK_INFO_INTRINSIC_HEIGHT
  // The error overlays the preview (post 1634117708345526), so it reserves no
  // height: a failure never changes the card's outline size.
  const showStatusOverlay = isError && !showSpinner
  const cardIntrinsicHeight = previewIntrinsicHeight + quickInfoIntrinsicHeight
  const cardIntrinsicStyle = getCanvasNodeCardIntrinsicStyle(nodeWidth, cardIntrinsicHeight)
  const displayError =
    sanitizeNodeError(resolveNodeErrorMessage(error, t), t, true) ||
    t('imageGeneratorNode.defaultError')
  // ── History ────────────────────────────────────────────────────────────────

  const historyEntries = useMemo(
    (): HistoryEntry[] => {
      void cacheKey
      return generatedImageHistoryToJson(out_image?.history)
    },
    [out_image, cacheKey]
  )
  const handleVariantDownload = useVariantImageDownload({ nodeId: id, history: historyEntries })
  const batchPending = isRunning ? (node?.get('batch_pending') ?? 0) : 0
  const modelLabel = getImageModelAlias(in_model)
    ?? IMAGE_MODEL_OPTIONS.find((o) => o.value === in_model)?.label
    ?? t('imageGeneratorNode.modelFallback')
  // Node card shows the model name only, like the other node cards; aspect
  // ratio and quality live in the config panel's summary bar.
  const configSummary = modelLabel

  useEffect(() => {
    canvasNodeIntrinsicSizeLog({
      event: 'image-card-intrinsic-size',
      node_id: id,
      node_type: 'imageGenerator',
      width: Math.round(nodeWidth),
      height: Math.round(cardIntrinsicHeight),
      preview_height: Math.round(previewIntrinsicHeight),
      quick_info_height: quickInfoIntrinsicHeight,
      status_overlay: showStatusOverlay,
      aspect_ratio: in_aspect_ratio,
      css_aspect_ratio: cssAspectRatio,
      has_asset_dimensions: Boolean(assetDisplayDimensions),
      show_spinner: showSpinner,
    })
  }, [
    assetDisplayDimensions,
    cardIntrinsicHeight,
    cssAspectRatio,
    id,
    in_aspect_ratio,
    nodeWidth,
    previewIntrinsicHeight,
    quickInfoIntrinsicHeight,
    showSpinner,
    showStatusOverlay,
  ])

  useEffect(() => {
    if (showSpinner) return
    quickInfoLayoutLog({
      event: 'quick-info-layout:render',
      trace_id: `quick-info-layout:${id}`,
      node_id: id,
      node_type: 'imageGenerator',
      targetId: id,
      targetType: 'quick-info-config-summary-background',
      changed: {
        node_width: nodeWidth,
        aspect_ratio: in_aspect_ratio,
        quality: in_quality,
        model_label: modelLabel,
        history_count: historyEntries.length,
      },
    })
  }, [
    id,
    nodeWidth,
    in_aspect_ratio,
    in_quality,
    modelLabel,
    historyEntries.length,
    showSpinner,
  ])

  // Variants grid base aspect: latest variant's persisted aspect_ratio when
  // available, otherwise the node's current cssAspectRatio. The variants grid
  // (and its column count) follow this base — a 9:16 latest variant gives a
  // 3-column portrait grid even if the user has since changed the node's
  // in_aspect_ratio. Older variants with a different source aspect get
  // cropped via the inner img/video's `object-cover`, mirroring the main
  // preview's behavior.
  const variantsGridAspect = useMemo(() => {
    const latest = historyEntries[historyEntries.length - 1]
    const aspectRatio = latest?.result?.metadata?.aspect_ratio
    return (aspectRatio && ASPECT_RATIO_MAP[aspectRatio]) || cssAspectRatio
  }, [historyEntries, cssAspectRatio])

  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const ynode = canvas?.getYNode?.(id)
      if (!ynode) {
        throw new Error(`ImageGeneratorNode ${id}: missing YDoc node for variant selection`)
      }
      const outImage = ynode.get('out_image')
      if (!(outImage instanceof Y.Map)) {
        throw new Error(`ImageGeneratorNode ${id}: out_image must be Y.Map for variant selection`)
      }
      const alreadyPicked = outImage.get('picked_index') === index
      const writePickedIndex = () => {
        if (outImage.get('picked_index') !== index) {
          outImage.set('picked_index', index)
        }
      }
      if (ynode.doc) {
        ynode.doc.transact(writePickedIndex, 'variant-selection')
      } else {
        writePickedIndex()
      }
      // RFC-0974: `canvas_variant_kept` — only on an actual change of the kept
      // variant (best-effort, consent-gated). variant_id = node id + index.
      if (!alreadyPicked && projectId) {
        trackVariantKeptChange(projectId, id, type, index)
      }
    },
    [id, historyEntries, canvas, projectId, type]
  )

  // ── Bottom panel state ────────────────────────────────────────────────────

  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  // Config-panel tabs exclude Variants — Variants now opens as a dedicated,
  // tab-less panel via the QuickInfo bar (post 1533007285123236). When
  // activeTab === 'variants' the panel renders in variants mode (hideTabBar +
  // canvas-zoom); otherwise the tab bar (Config/Refine/Quality) is shown.
  const tabs = useMemo(() => {
    return IMAGE_GEN_TABS.filter((t) => t.id !== 'variants')
  }, [])
  const variantsMode = activeTab === 'variants'
  // Dedicated Variants panel layout: tiles match the preview width; panel sized
  // for 1x3 (9:16) / 2x2 (16:9) without scroll, grows then scrolls beyond.
  const variantLayout = useMemo(
    () =>
      computeVariantPanelLayout(
        variantsGridAspect,
        nodeWidth,
        historyEntries.length + batchPending,
      ),
    [variantsGridAspect, nodeWidth, historyEntries.length, batchPending],
  )

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning: isGenerating,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })
  const selectSingleNode = useSelectSingleCanvasNode()

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
      variantsPanelLog({
        event: 'bottom-panel:open',
        trace_id: `variants-panel:${id}`,
        node_id: id,
        node_type: 'imageGenerator',
        mode: tab === 'variants' ? 'variants' : 'config',
        active_tab: tab,
      })
    },
    [id, selectSingleNode]
  )

  // ── Lightbox ───────────────────────────────────────────────────────────────

  const [isModalOpen, setIsModalOpen] = useState(false)

  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isModalOpen)

  // ── Config updates ─────────────────────────────────────────────────────────

  // Write one or more config fields atomically. canvas.updateNodeData wraps the
  // whole patch in a single ydoc.transact (useYCanvas.writeNodeData), so a
  // multi-field patch is one Yjs transaction + one undo step — remote clients
  // never observe a half-applied snap. Single-field callers pass a one-key patch.
  const updateConfig = useCallback(
    (patch: NodeDataRecord<'imageGenerator'>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n))
      )
      canvas?.updateNodeData({ id, type: 'imageGenerator' }, patch)
    },
    [id, setNodes, canvas]
  )

  // What the user asked for, kept across model switches. A switch that has to
  // change a value records it in `forcedRef`; while the field still holds that
  // forced value, the next switch snaps from the intent instead, so going
  // Gemini(2:1) -> Nano Pro(forced 16:9) -> GPT restores 2:1. Any other value
  // (a user pick, a remote edit) is taken as the new intent.
  const intentRef = useRef({ aspectRatio: in_aspect_ratio, quality: in_quality })
  const forcedRef = useRef<{ aspectRatio?: AspectRatio; quality?: string }>({})
  const [constraintNotice, setConstraintNotice] = useState<ImageConstraintNotice | null>(null)

  // Once a field leaves its forced value, the forced marker is spent, so a later
  // remote write back to that value reads as intent rather than as still forced.
  useEffect(() => {
    const forced = forcedRef.current
    if (forced.aspectRatio !== undefined && forced.aspectRatio !== in_aspect_ratio) {
      forcedRef.current = { ...forcedRef.current, aspectRatio: undefined }
    }
    if (forced.quality !== undefined && forced.quality !== in_quality) {
      forcedRef.current = { ...forcedRef.current, quality: undefined }
    }
  }, [in_aspect_ratio, in_quality])

  const handleAspectRatioChange = useCallback(
    (ratio: AspectRatio) => {
      intentRef.current = { ...intentRef.current, aspectRatio: ratio }
      forcedRef.current = { ...forcedRef.current, aspectRatio: undefined }
      setConstraintNotice((n) => (n?.aspect ? { ...n, aspect: undefined } : n))
      updateConfig({ in_aspect_ratio: ratio })
    },
    [updateConfig],
  )

  const handleQualityChange = useCallback(
    (quality: string) => {
      intentRef.current = { ...intentRef.current, quality }
      forcedRef.current = { ...forcedRef.current, quality: undefined }
      setConstraintNotice((n) => (n?.quality ? { ...n, quality: undefined } : n))
      updateConfig({ in_quality: quality })
    },
    [updateConfig],
  )

  // Model change: snap the user's intended quality/aspect to the new model's
  // supported set and write model+aspect+quality in ONE atomic patch so no
  // client sees the new model paired with an unsupported aspect/quality.
  const handleModelChange = useCallback(
    (newModel: string) => {
      const intent = {
        aspectRatio:
          forcedRef.current.aspectRatio === in_aspect_ratio
            ? intentRef.current.aspectRatio
            : in_aspect_ratio,
        quality:
          forcedRef.current.quality === in_quality ? intentRef.current.quality : in_quality,
      }
      intentRef.current = intent
      const snapped = snapOnModelChange(in_model, newModel, intent)
      const patch: NodeDataRecord<'imageGenerator'> = { in_model: newModel }
      if (snapped.aspectRatio !== in_aspect_ratio) patch.in_aspect_ratio = snapped.aspectRatio
      if (snapped.quality !== in_quality) patch.in_quality = snapped.quality
      updateConfig(patch)

      const aspectForced = snapped.aspectRatio !== intent.aspectRatio
      // A model with a fixed resolution has no quality choice to lose.
      const qualityForced =
        snapped.quality !== intent.quality && getImageConstraints(newModel).qualityOptions !== null
      forcedRef.current = {
        aspectRatio: aspectForced ? snapped.aspectRatio : undefined,
        quality: snapped.quality !== intent.quality ? snapped.quality : undefined,
      }
      setConstraintNotice(
        aspectForced || qualityForced
          ? {
              model: newModel,
              ...(aspectForced
                ? { aspect: { from: intent.aspectRatio, to: snapped.aspectRatio } }
                : {}),
              ...(qualityForced ? { quality: { from: intent.quality, to: snapped.quality } } : {}),
            }
          : null,
      )
      imageSelectorGatingLog({
        event: 'model-change-snap',
        node_id: id,
        fromModel: in_model,
        toModel: newModel,
        aspectBefore: in_aspect_ratio,
        aspectIntent: intent.aspectRatio,
        aspectAfter: snapped.aspectRatio,
        qualityBefore: in_quality,
        qualityIntent: intent.quality,
        qualityAfter: snapped.quality,
        aspectForced,
        qualityForced,
      })
    },
    [id, in_model, in_quality, in_aspect_ratio, updateConfig],
  )

  // Correct already-saved stale configs (e.g. a GPT Image 2 node persisted with
  // 2K + 9:16 before per-model gating existed) when the node mounts or its
  // model changes. Guarded + idempotent so StrictMode's double-invoke is a
  // no-op and there is no write storm.
  useEffect(() => {
    const snapped = snapToModelConstraints(in_model, {
      quality: in_quality,
      aspectRatio: in_aspect_ratio,
    })
    if (snapped.aspectRatio === in_aspect_ratio && snapped.quality === in_quality) return
    const patch: NodeDataRecord<'imageGenerator'> = {}
    if (snapped.aspectRatio !== in_aspect_ratio) patch.in_aspect_ratio = snapped.aspectRatio
    if (snapped.quality !== in_quality) patch.in_quality = snapped.quality
    updateConfig(patch)
    imageSelectorGatingLog({
      event: 'stale-config-snap',
      node_id: id,
      model: in_model,
      aspectBefore: in_aspect_ratio,
      aspectAfter: snapped.aspectRatio,
      qualityBefore: in_quality,
      qualityAfter: snapped.quality,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [in_model])

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      updateConfig({ in_name: newName })
    },
    [updateConfig],
  )

  // ── Generate handler ───────────────────────────────────────────────────────

  const showInsufficientSparks = useShowInsufficientSparks()
  // Per-node 1s cooldown so a fast double-tap can't fire /execute-node twice.
  const { isLocked, run } = useClickLock(1000)
  const generateLocked = isLocked(id)

  useEffect(() => {
    const failure = generationGrace.failure
    if (!failure) return
    generationGrace.clearFailure()
    const failureMessage = failure.code === 'submission_unconfirmed'
      ? t('generationGracePeriod.submissionUnconfirmed')
      : failure.message
    if (
      failure.code === 'insufficient_sparks' &&
      projectId
    ) {
      showInsufficientSparks({
        nodeId: id,
        nodeType: 'image_generator',
        projectId,
      })
      return
    }
    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? { ...n, data: { ...n.data, status: 'error', error: failureMessage } }
          : n,
      ),
    )
  }, [generationGrace, id, projectId, setNodes, showInsufficientSparks, t])

  const handleGenerate = useCallback(async (retryCount: number = 1, analytics?: ExecuteNodeAnalytics) => {
    // Guard: double-submit or pending_execution/pending_cost_confirmation (orchestrator will fire it)
    if (showSpinner) return

    // Guard: missing projectId (no silent failure)
    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: encodeNodeError({ code: 'node.missingProject' }) } }
            : n
        )
      )
      return
    }

    void run(id, async () => {
      try {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
              : n,
          ),
        )
        generationGrace.schedule(retryCount, {
          ...analytics,
          generationMode: generateModeFor(historyEntries.length > 0),
          modelSource: modelSourceOf(in_model),
        })
      } catch (err) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [generationGrace, id, projectId, run, setNodes, showSpinner, historyEntries.length, in_model])

  // ── Download handler ───────────────────────────────────────────────────────

  const onDownload = useCallback(() => {
    if (imagePath) {
      const { filename } = buildAssetDownloadFilename({
        canvasNodes: getNodes(),
        canvasEdges: edges,
        nodeId: id,
        assetPath: imagePath,
        mediaType: 'image',
        variantHistory: historyEntries,
        pickedIndex,
      })
      handleDownload(imagePath, filename)
    }
  }, [imagePath, handleDownload, getNodes, edges, id, historyEntries, pickedIndex])
  useRegisterNodeContextAction(id, 'download', onDownload, Boolean(imagePath))

  // ── Refinement handler ────────────────────────────────────────────────────

  const [feedbackText, setFeedbackText] = useState('')

  // RFC-1115 inline Refine annotation. Only the image_generator schema declares
  // the `annotated_image_path` state field the run argument is sent as; the
  // legacy generatedImageWithHistory type shares this component but not that
  // field, so it keeps the text-only Refine.
  const supportsRefineAnnotation = type === 'imageGenerator'

  const handleRefineSubmit = useCallback(async (
    retryCount: number = 1,
    options?: { annotatedImagePath?: string },
  ) => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    refineVariantCountLog({
      event: 'refine-handler',
      nodeType: 'imageGenerator',
      node_id: id,
      retryCount,
    })

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                status: 'running',
                progressMessage: t('imageGeneratorNode.refiningImage'),
                error: undefined,
              },
            }
          : n
      )
    )

    try {
      // Write feedback to YDoc before executing
      if (feedbackText.trim()) {
        node?.writeInFeedback(feedbackText.trim())
      }
      // Forward the selected variant count so a "4x" refine fans out to 4
      // generations (mirrors the Generate/Config path). Without this the
      // count is dropped and the backend defaults to 1.
      // The annotation is a per-run argument, not node data: always sent (empty
      // when nothing was drawn) so a previous drawing never leaks into a later
      // text-only refine. See useFeedbackMode's RefineRunOptions.
      await executeNode(projectId, id, {
        count: retryCount,
        canvasId,
        ...(supportsRefineAnnotation
          ? { stateUpdates: { annotated_image_path: options?.annotatedImagePath ?? '' } }
          : {}),
        analytics: { nodeType: type, tab: 'refine', generationMode: 'refine', modelSource: modelSourceOf(in_model) },
      })
      setFeedbackText('')
    } catch (err) {
      // Insufficient sparks goes to the workspace-level modal; revert the
      // optimistic 'running' status to 'idle' so the node card doesn't look
      // stuck. A multi-variant refine (retryCount > 1) makes this far more
      // likely, so handle it the same way as the Generate/Config path.
      if (err instanceof InsufficientSparksError) {
        showInsufficientSparks({
            balance: err.balance,
            required: err.required,
            nodeId: id,
            nodeType: 'image_generator',
            projectId,
          })
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
              : n
          )
        )
        return
      }
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n
        )
      )
    }
  }, [projectId, canvasId, id, feedbackText, status, setNodes, node, showInsufficientSparks, t, supportsRefineAnnotation, type, in_model])

  // ── Reference handling for ConfigTabContent ───────────────────────────────

  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [canvas, id]
  )

  // ── Toolbar actions ────────────────────────────────────────────────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      {
        key: 'addToChat',
        label: t('imageGeneratorNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      annotate.toolbarAction,
      {
        key: 'duplicate',
        label: t('imageGeneratorNode.duplicate'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('imageGeneratorNode.delete'),
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [duplicateMenuItems, handleDelete, handleSaveToLibrary, saveState, saveLabel, handleAddToChat, annotate.toolbarAction, t]
  )

  // ── Panel visibility ──────────────────────────────────────────────────────

  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  // ── Render ──────────────────────────────────────────────────────────────────

  if (lightweightNodeChrome && !selected && !dragging && !isHoveringLightweightNodeChrome) {
    return (
      <div
        data-testid="image-generator-lightweight-host"
        className="w-fit"
        onPointerEnter={() => setIsHoveringLightweightNodeChrome(true)}
        onPointerLeave={() => setIsHoveringLightweightNodeChrome(false)}
      >
        <FlatIdleNodeCard
          nodeId={id}
          testId="image-generator-flat-node"
          label={displayName}
          fallbackLabel={t('imageGeneratorNode.imageGenerator')}
          typeIcon={NODE_TYPE_ICON.imageGenerator}
          width={nodeWidth}
          status={generationGrace.isLocallyGenerating ? 'running' : status}
          error={isError ? displayError : undefined}
          progressMessage={loadingMessage}
          metaLoading={metaLoading}
          handles={HANDLES}
          mediaKind={imageUrl ? 'image' : 'none'}
          mediaUrl={imageUrl}
          outputVariantIndex={pickedIndex}
          aspectRatio={cssAspectRatio}
          placeholderLabel={t('imageGeneratorNode.imageGenerator')}
          compactHandles
        />
      </div>
    )
  }

  return (
    <>
      <div
        className="relative"
        onPointerEnter={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(true) : undefined}
        onPointerLeave={lightweightNodeChrome ? () => setIsHoveringLightweightNodeChrome(false) : undefined}
      >
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* RFC-1115 annotation editor. Mounted at the NODE ROOT, not inside the
            image container: that container is `overflow: hidden`, which clips the
            tool palette (wider than a 315px node card) and leaves its buttons
            unclickable behind the ReactFlow pane. The root has no clipping
            ancestor, so the palette can overhang the card. */}
        {annotate.isOpen && fullImageUrl && (
          <AnnotationEditorOverlay
            imageUrl={fullImageUrl}
            altText={t('imageGeneratorNode.altGenerated')}
            traceId={annotate.traceId}
            nodeId={id}
            isSaving={annotate.isSaving}
            onCancel={annotate.close}
            onSave={annotate.save}
          />
        )}

        {/* Floating label above the card */}
        <FloatingNodeLabel name={displayName || ''} fallback={t('imageGeneratorNode.imageGenerator')} typeIcon={NODE_TYPE_ICON.imageGenerator} onRename={handleRename} />

        {/* No layout-affecting border in any state: the spinner emphasis is a
            non-layout box-shadow so a rendering card is exactly as tall as a
            completed one (post 1539881217769176). A 2px `border-2` here would
            add ~4px of auto-height only while rendering. */}
        <div
          className={`canvas-node-card-contained ${NODE_CARD_RADIUS_CLASS} overflow-hidden transition-all duration-150`}
          style={{
            width: nodePresentationWidth(nodeWidth),
            ...cardIntrinsicStyle,
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : isError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : showSpinner
                  ? '0 0 0 2px rgba(234,179,8,0.6)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
          }}
          data-generation-phase={generationGrace.phase ?? undefined}
        >
          {/* Handles */}
          {handlesVisible && HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Main content area — top corners rounded only; bottom sits flush against QuickInfoBar (post 1579929167097714) */}
          <div className="relative rounded-t-lg overflow-hidden">
            {metaLoading ? (
              /* Generating / queued state */
              <div
                className="relative w-full bg-canvas"
                style={{ aspectRatio: cssAspectRatio, ...previewStyle }}
              >
                <MetaLoadingSurface
                  {...metaLoading}
                  projectId={projectId}
                  nodeId={id}
                  aspectRatio={cssAspectRatio}
                  presentation="full"
                >
                  {cancelDuringGraceButton}
                </MetaLoadingSurface>
              </div>
            ) : imageUrl ? (
              /* Image state — with optional error overlay */
              <div
                ref={previewRef}
                className="relative group"
                style={{ aspectRatio: cssAspectRatio, overflow: 'hidden', ...previewStyle }}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    style={{ objectFit: mediaFit }}
                    alt={t('imageGeneratorNode.altGenerated')}
                    decoding="async"
                    className={`w-full h-full object-cover block transition-all duration-300 ease-out ${
                      !selected ? 'group-hover:scale-[1.02]' : ''
                    }`}
                    onLoad={(event) => {
                      onMediaSettled()
                      setAssetDisplayDimensions(
                        snapNaturalDimensionsToDisplayDimensions(
                          event.currentTarget.naturalWidth,
                          event.currentTarget.naturalHeight,
                        ),
                      )
                    }}
                    onError={onMediaSettled}
                  />
                ) : (
                  // Same box as the image so the card never resizes when the
                  // asset lands — only its contents change.
                  <div className="w-full h-full bg-surface-1" aria-hidden="true" />
                )}

                {/* Overlay controls — stay available on a failed re-run: the
                    status strip covers only the bottom edge, so the previous
                    image can still be expanded or downloaded. */}
                <div
                    className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsModalOpen(true)
                    }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title={t('imageGeneratorNode.expand')}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDownload()
                    }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title={t('imageGeneratorNode.download')}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </button>
                </div>

              </div>
            ) : isError ? (
              /* Error state (no previous image) — the message itself is the
                 shared status overlay below. */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-6"
                style={{ aspectRatio: cssAspectRatio, ...previewStyle }}
              >
                <AlertTriangle className="w-8 h-8 text-foreground/15" strokeWidth={1.5} />
              </div>
            ) : (
              /* Empty placeholder */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center"
                style={{ aspectRatio: cssAspectRatio, ...previewStyle }}
              >
                <svg
                  className="w-8 h-8 text-foreground/10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xxs text-foreground/20 mt-1">{t('imageGeneratorNode.imageGenerator')}</span>
              </div>
            )}

            {/* Status overlay — floats over the bottom of the preview whenever
                the node is in error, with or without a previous image (post
                1634117708345526). */}
            {showStatusOverlay && (
              <NodeStatusMessage message={displayError} testId="image-card-error" />
            )}
          </div>

          {/* Quick Info Bar -- 2 columns: Config | Variants. Rendered in every
              state (including while rendering/queued) so the card height matches
              a completed card (post 1539881217769176). Still gated on node chrome
              being rendered (lightweight mode hides it). */}
          {shouldRenderNodeChrome && (
            <QuickInfoBar
              configSummary={configSummary}
              references={currentRefs}
              projectId={projectId}
              cacheBust={cacheBust}
              variantCount={historyEntries.length}
              onOpenConfig={() => openPanelToTab('config')}
              onOpenVariants={() => openPanelToTab('variants')}
            />
          )}
        </div>

        {/* Unified Bottom Panel (both standalone and group mode) */}
        <UnifiedBottomPanel
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
          hideTabBar={variantsMode}
          // Config/Refine match the Character/Location designer panel width;
          // height keeps the shared 340px default, as those designers do.
          minWidth={variantsMode ? variantLayout.minWidth : DESIGNER_PANEL_MIN_WIDTH}
          contentHeight={variantsMode ? variantLayout.contentHeight : undefined}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="imageGenerator"
                  prompt={in_prompt}
                  onPromptChange={(value) => updateConfig({ in_prompt: value })}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig({ in_style: styleId })}
                  aspectRatio={in_aspect_ratio}
                  quality={in_quality}
                  model={in_model}
                  onAspectRatioChange={handleAspectRatioChange}
                  onQualityChange={handleQualityChange}
                  onModelChange={handleModelChange}
                  imageConstraintNotice={constraintNotice}
                  isRunning={showSpinner || generateLocked}
                  onGenerate={(retryCount) => {
                    void handleGenerate(retryCount, { tab: 'config', referenceCount: currentRefs.length })
                  }}
                  onCancel={generationGrace.isWaiting && !isRunning ? generationGrace.cancelPending : undefined}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  nodeId={id}
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={handleRefineSubmit}
                  isRunning={isRunning}
                  nodeType="imageGenerator"
                  selectedStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig({ in_style: styleId })}
                  onModelChange={handleModelChange}
                  model={in_model}
                  quality={in_quality}
                  aspectRatio={in_aspect_ratio}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  targetBackendType="image_generator"
                  annotationSourcePath={supportsRefineAnnotation ? imagePath : undefined}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  onDownloadVariant={handleVariantDownload}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  nodeId={id}
                  nodeType="imageGenerator"
                  agentColor={agentColor}
                  aspectRatio={variantsGridAspect}
                  maxWidth={nodeWidth}
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            data-testid="image-generator-lightbox"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt={t('imageGeneratorNode.altFullSize')}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

ImageGeneratorNode.displayName = 'ImageGeneratorNode'
export default ImageGeneratorNode
export { ASPECT_RATIO_OPTIONS }
import { useNodePresentationWidth, useNodePresentationPreview } from '../../NodePresentationContext'
